import {initAligningGuidelines} from '@ThirdParty/aligning_guidelines';
import type {noop} from '@Utils/general.util';
import {ActiveSelection, Canvas, type FabricObject} from '@postermywall/fabricjs-2';
import type {Page} from './page.class';
import Emitter from '@/services/event-emitter';
import {initCenteringGuidelines} from '@Libraries/centering-guide-lines-library';
import {GUIDELINES_COLOR, GUIDELINES_MARGIN} from '@PosterWhiteboard/page/page.types';
import {initCanvasEdgeGuidelines} from '@Libraries/canvas-edge-guidelines';

export class RenderAlignmentGuidelines {
  public page: Page;
  public disableGuides: (typeof noop)[] = [];

  constructor(page: Page) {
    this.page = page;
    Emitter.on('userPreferences:areAlignmentGuidesEnabledUpdated', this.onAreAlignmentGuidesEnabledUpdated.bind(this));
    if (window.PMW.redux.store.getState().userPreferences.areAlignmentGuidesEnabled) {
      this.enableAligningGuidelines();
    }
  }

  private areAlignmentGuidesInitialized(): boolean {
    return this.disableGuides.length > 0;
  }

  private onAreAlignmentGuidesEnabledUpdated(): void {
    if (window.PMW.redux.store.getState().userPreferences.areAlignmentGuidesEnabled) {
      this.enableAligningGuidelines();
    } else {
      this.disableAligningGuidelines();
    }
  }

  public enableAligningGuidelines(): void {
    if (this.areAlignmentGuidesInitialized()) {
      return;
    }

    if (this.page.fabricCanvas instanceof Canvas) {
      this.disableGuides.push(
        initAligningGuidelines(this.page.fabricCanvas, {
          color: GUIDELINES_COLOR,
          margin: GUIDELINES_MARGIN,
          getObjectsByTarget: this.getObjectsByTarget.bind(this),
        })
      );
      this.disableGuides.push(initCenteringGuidelines(this.page.fabricCanvas));
      this.disableGuides.push(initCanvasEdgeGuidelines(this.page.fabricCanvas));
    }
  }

  private getObjectsByTarget(target: FabricObject): Set<FabricObject> {
    const objects = new Set<FabricObject>();
    const canvas = target.canvas;
    if (!canvas) return objects;
    const children = target instanceof ActiveSelection ? target.getObjects() : [target];

    canvas.forEachObject((o) => {
      if (!o.isOnScreen()) return;
      if (!o.visible) return;
      if (!o.__PMWID) return;
      objects.add(o);
    });

    this.deleteObjectsByList(objects, children);
    return objects;
  }

  private deleteObjectsByList(objects: Set<FabricObject>, list: FabricObject[]): void {
    for (const target of list) {
      objects.delete(target);
    }
  }

  public disableAligningGuidelines(): void {
    for (const disableGuide of this.disableGuides) {
      disableGuide();
    }
    this.disableGuides = [];
  }
}
